import {useNavigate, useParams} from "react-router-dom";
import {Alert, Autocomplete, Box, Divider, Stack} from "@mui/material";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import {ResourceCategoryService} from "crqlar-react-common/dist/services/resourceCategory/resourceCategory.service";
import {ResourceCategory} from "crqlar-react-common/dist/models/resourceCategory/resourceCategory.model";
import SettingsEntityHeader from "../layout/SettingsEntityHeader";
import {isRequiredTextMissing} from "src/utils/requiredField.util";
import DangerZone from "crqlar-react-common/dist/components/DangerZone";
import ImageUploadField from "crqlar-react-common/dist/components/ImageUploadField";
import Property from "crqlar-react-common/dist/components/Property";
import LocalizedTextField from "crqlar-react-common/dist/components/LocalizedTextField";
import {useAllSectors} from "crqlar-react-common/dist/utils/hooks/sectors/useSectors";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";

const ResourceCategoryDetails = () => {

    //region State

    useTrackScreenVisit('system resource category details')

    const {t} = useTranslation()
    const {id} = useParams()
    const navigate = useNavigate()

    const sectors = useAllSectors()

    const resourceCategoryData = useEntity({
        get: async () => {
            if (id && id !== 'new') {
                return ResourceCategoryService.getResourceCategoryById(id)
            } else {
                return {}
            }
        },
        create: ResourceCategoryService.createResourceCategory,
        update: ResourceCategoryService.updateResourceCategory,
        delete: (id) => ResourceCategoryService.deleteResourceCategory({id: [id]}),
        onDeleted: () => navigate('/system/resourceCategories', {replace: true}),
        onSaved: (resourceCategory, wasCreateAction, prev) => {
            if (wasCreateAction) {
                navigate(`/system/resourceCategories/${resourceCategory.id}`, {replace: true})
            }
        },
        isValid: (resourceCategory) => {
            return !!resourceCategory.name
        },
        cleanupBeforeSave: (resourceCategory) => {
            return {
                ...resourceCategory,
                name: resourceCategory.name?.trim()
            }
        }
    }, id)

    const resourceCategory = resourceCategoryData.item
    const disabled = !!resourceCategory.managedExternally
    const setResourceCategory = resourceCategoryData.setItem
    const nameMissing = !disabled && isRequiredTextMissing(resourceCategoryData, resourceCategory.name)

    //endregion Sate

    //region Handlers

    const updateField = (field: keyof ResourceCategory, value: any) => {
        setResourceCategory({...resourceCategory, [field]: value})
    }

    //endregion Handlers

    //region UI

    const headerSection = (
        <SettingsEntityHeader
            title={resourceCategory.name}
            data={resourceCategoryData}
            back={() => navigate('/system/resourceCategories')}
            restrictEdit={disabled}
        />
    )

    const titleSection = (
        <Stack gap={2}>
            {
                disabled &&
                <Alert
                    variant={'outlined'}
                    severity={'warning'}
                    sx={{py: 0, lineHeight: 1, alignItems: 'center'}}
                >
                    {t('This entity is managed by an external system and thus cannot be edited here.')}
                </Alert>
            }

            <ImageUploadField
                id={'image-picker'}
                image={resourceCategory.media}
                setImage={(image) => updateField('media', image)}
                ratio={'landscape'}
                disabled={disabled}
            />
        </Stack>
    )

    const basicInfoSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Basic info')}
            </Typography>

            <Stack gap={1}>
                <Property
                    title={t('Name')}
                    tooltip={t('Display label used to group resources in setup and booking screens.')}
                >
                    <TextField
                        id={'name'}
                        size={'small'}
                        value={resourceCategory.name ?? ''}
                        onChange={(e) => updateField('name', e.target.value)}
                        fullWidth
                        required
                        error={nameMissing}
                        disabled={disabled}
                    />
                </Property>

                <Property
                    title={t('Sectors')}
                    tooltip={t('Sectors where resources in this category can be used.')}
                >
                    <Autocomplete
                        id={'sectors'}
                        multiple
                        options={sectors}
                        getOptionLabel={(option: any) => option.name || ''}
                        value={resourceCategory.sectorIds?.map(id => sectors.find(c => c.id === id)).filter(Boolean) as any[] || []}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                fullWidth
                                size={'small'}
                            />
                        )}
                        fullWidth
                        onChange={(e, value) => {
                            updateField('sectorIds', value.map((v: any) => v.id))
                        }}
                        disabled={disabled}
                    />
                </Property>
            </Stack>
        </Stack>
    )

    const translationsSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Translations')}
            </Typography>

            <Stack gap={1}>
            <Property
                title={t('Name translations')}
                tooltip={t('Localized category names shown in guest-facing content and translated interfaces.')}
            >
                <LocalizedTextField
                    id={'nameLocalized'}
                    size={'small'}
                    value={resourceCategory.nameLocalized}
                    onChange={(e) => updateField('nameLocalized', e)}
                    fullWidth
                    required
                    disabled={disabled}
                />
            </Property>

            <Property
                title={t('Description translations')}
                tooltip={t('Localized category descriptions shown where this resource category is presented to guests.')}
            >
                <LocalizedTextField
                    id={'descriptionLocalized'}
                    size={'small'}
                    value={resourceCategory.descriptionLocalized}
                    onChange={(e) => updateField('descriptionLocalized', e)}
                    fullWidth
                    required
                    multiline
                    rows={3}
                    disabled={disabled}
                />
            </Property>
            </Stack>
        </Stack>
    )

    const dangerZoneSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <DangerZone
                title={t('Delete resource category')}
                description={t('This permanently deletes the category. This action cannot be undone.')}
                buttonLabel={t('Delete')}
                loading={resourceCategoryData.saving}
                onDelete={() => resourceCategoryData.removeNow()}
            />
            <Typography variant={'body2'} color={'text.secondary'} textAlign={'start'}>
                {t('Last updated')}: {resourceCategory.updateDate?.toLocaleString() || '-'}
            </Typography>
        </Stack>
    )

    //endregion UI

    return (
        <Box sx={{width: '100%', pb: 8}}>
            {headerSection}

            <Stack
                sx={{
                    width: '100%',
                    maxWidth: '900px',
                    mx: 'auto',
                    mt: 3,
                    gap: 6,
                }}
            >
                {titleSection}
                {basicInfoSection}
                <Divider/>
                {translationsSection}

                {
                    !!resourceCategory.id &&
                    <>
                        <Divider/>
                        {dangerZoneSection}
                    </>
                }
            </Stack>
        </Box>
    );
}

export default ResourceCategoryDetails;
